(() => {
  const CONFIG = {
    animateCards: true,
    animatePanels: true,
    exitMs: 120,
    enterMs: 180,
    overlapMs: 0,
    fallbackBufferMs: 80,
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const normalizeCategory = (value) => (value || '').trim();

  const safeSetAriaSelected = (btn, isSelected) => {
    btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    btn.tabIndex = isSelected ? 0 : -1;
  };

  const onTransitionEndOnce = (el, cb, fallbackMs) => {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      cb();
    };

    const onEnd = (e) => {
      if (e.target !== el) return;
      finish();
    };

    el.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, fallbackMs);
  };

  const pinPanelForExit = (panel, container) => {
    if (!panel || !container) return () => {};

    const panelRect = panel.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const top = panelRect.top - containerRect.top + container.scrollTop;
    const left = panelRect.left - containerRect.left + container.scrollLeft;
    const width = panelRect.width;

    const prev = {
      position: panel.style.position,
      top: panel.style.top,
      left: panel.style.left,
      width: panel.style.width,
      zIndex: panel.style.zIndex,
    };

    panel.style.position = 'absolute';
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.style.width = `${width}px`;
    panel.style.zIndex = '2';

    return () => {
      panel.style.position = prev.position;
      panel.style.top = prev.top;
      panel.style.left = prev.left;
      panel.style.width = prev.width;
      panel.style.zIndex = prev.zIndex;
    };
  };

  // Shared state to communicate between drag and click handlers
  let globalIsDragging = false;

  const initScrollFeatures = (root) => {
    const navContainer = root.querySelector('.compare-category-nav');
    const leftShadow = root.querySelector('.scroll-shadow--left');
    const rightShadow = root.querySelector('.scroll-shadow--right');

    if (!navContainer) return;

    const updateShadows = () => {
      if (!leftShadow || !rightShadow) return;

      const scrollLeft = navContainer.scrollLeft;
      const scrollWidth = navContainer.scrollWidth;
      const clientWidth = navContainer.clientWidth;

      const hasOverflow = scrollWidth > clientWidth;
      const isAtStart = scrollLeft <= 5;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

      if (!hasOverflow) {
        leftShadow.classList.remove('is-visible');
        rightShadow.classList.remove('is-visible');
        return;
      }

      if (isAtStart) leftShadow.classList.remove('is-visible');
      else leftShadow.classList.add('is-visible');

      if (isAtEnd) rightShadow.classList.remove('is-visible');
      else rightShadow.classList.add('is-visible');
    };

    const enableDragScroll = () => {
      let isDown = false;
      let startX;
      let scrollLeft;

      navContainer.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - navContainer.offsetLeft;
        scrollLeft = navContainer.scrollLeft;
      });

      const stopDragging = () => {
        isDown = false;
        navContainer.classList.remove('is-dragging');

        // Short timeout to ensure click handler sees the flag before we reset
        setTimeout(() => {
          globalIsDragging = false;
        }, 50);
      };

      navContainer.addEventListener('mouseleave', stopDragging);
      navContainer.addEventListener('mouseup', stopDragging);

      navContainer.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();

        const x = e.pageX - navContainer.offsetLeft;
        const walk = (x - startX) * 2;

        // Only trigger drag state if moved more than a few pixels to avoid blocking simple clicks
        if (Math.abs(walk) > 5) {
          globalIsDragging = true;
          navContainer.classList.add('is-dragging');
          navContainer.scrollLeft = scrollLeft - walk;
        }
      });
    };

    navContainer.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', updateShadows);
    updateShadows();
    enableDragScroll();
  };

  const initCompareBikes = (root) => {
    const buttons = Array.from(root.querySelectorAll('[data-category-btn]'));
    const panels = Array.from(root.querySelectorAll('[data-category-panel]'));
    if (!buttons.length || !panels.length) return;

    const container = root.querySelector('.compare-lists');

    const reduced = prefersReducedMotion();
    const animatePanels = CONFIG.animatePanels && !reduced;
    const animateCards = CONFIG.animateCards;

    const getPanelByCategory = (category) =>
      panels.find((p) => p.dataset.categoryPanel === category);

    const getActivePanel = () => panels.find((p) => !p.hidden);

    const setActiveLabel = (label) => {
      const labelEl = root.querySelector('[data-active-category-label]');
      if (labelEl) labelEl.textContent = label;
    };

    const setButtonStates = (activeCategory) => {
      buttons.forEach((btn) => {
        const isActive = normalizeCategory(btn.dataset.categoryBtn) === activeCategory;
        btn.classList.toggle('is-active', isActive);
        safeSetAriaSelected(btn, isActive);

        if (animateCards) {
          const card = btn.querySelector('.collection-card');
          if (card) card.classList.toggle('is-active', isActive);
        }
      });
    };

    const cleanupInlinePinStyles = (panel) => {
      panel.style.position = '';
      panel.style.top = '';
      panel.style.left = '';
      panel.style.width = '';
      panel.style.zIndex = '';
    };

    let timers = [];

    const clearTimers = () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers = [];
    };

    const hardSwapPanels = (activeCategory) => {
      clearTimers();

      panels.forEach((panel) => {
        const isActive = panel.dataset.categoryPanel === activeCategory;

        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        panel.classList.remove('is-exiting');

        cleanupInlinePinStyles(panel);
      });
    };

    const animatedControlledFade = (nextCategory) => {
      clearTimers();

      const current = getActivePanel();
      const next = getPanelByCategory(nextCategory);
      if (!next || next === current) return;

      if (!current) {
        panels.forEach((p) => {
          const isActive = p === next;

          p.hidden = !isActive;
          p.classList.toggle('is-active', isActive);
          p.classList.remove('is-exiting');

          cleanupInlinePinStyles(p);
        });
        return;
      }

      cleanupInlinePinStyles(next);
      next.hidden = false;
      next.classList.remove('is-exiting');
      next.classList.remove('is-active');
      next.style.zIndex = '1';

      let unpin = () => {};
      current.classList.add('is-active');
      current.classList.remove('is-exiting');

      if (container) unpin = pinPanelForExit(current, container);

      current.getBoundingClientRect();
      current.classList.add('is-exiting');

      const startEnterAfter = Math.max(0, CONFIG.exitMs - CONFIG.overlapMs);

      timers.push(
        window.setTimeout(() => {
          next.classList.add('is-active');
        }, startEnterAfter)
      );

      const totalExitWait = CONFIG.exitMs + CONFIG.fallbackBufferMs;

      onTransitionEndOnce(
        current,
        () => {
          current.hidden = true;
          current.classList.remove('is-exiting', 'is-active');
          unpin();
          cleanupInlinePinStyles(current);
        },
        totalExitWait
      );
    };

    const activate = (category) => {
      const nextCategory = normalizeCategory(category);
      if (!nextCategory) return;

      const btn = buttons.find((b) => normalizeCategory(b.dataset.categoryBtn) === nextCategory);
      const nextPanel = getPanelByCategory(nextCategory);
      if (!btn || !nextPanel) return;

      setButtonStates(nextCategory);
      setActiveLabel(btn.dataset.categoryLabel || '');

      if (animatePanels) animatedControlledFade(nextCategory);
      else hardSwapPanels(nextCategory);
    };

    const initialPanel = getActivePanel();
    const initialCategory =
      (initialPanel && initialPanel.dataset.categoryPanel) ||
      normalizeCategory(buttons[0].dataset.categoryBtn);

    activate(initialCategory);

    // Click (delegated)
    root.addEventListener(
      'click',
      (e) => {
        // If we were just dragging, do NOT switch categories
        if (globalIsDragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const btn = e.target.closest('[data-category-btn]');
        if (!btn || !root.contains(btn)) return;
        e.preventDefault();
        activate(btn.dataset.categoryBtn);
      },
      true
    ); // Use capture phase to intercept early if needed

    // Keyboard support
    root.addEventListener('keydown', (e) => {
      const activeEl = document.activeElement;
      const currentBtn =
        activeEl && activeEl.matches && activeEl.matches('[data-category-btn]') ? activeEl : null;
      if (!currentBtn) return;

      const key = e.key;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

      e.preventDefault();

      const idx = buttons.indexOf(currentBtn);
      if (idx < 0) return;

      let nextIdx = idx;
      if (key === 'ArrowLeft') nextIdx = (idx - 1 + buttons.length) % buttons.length;
      if (key === 'ArrowRight') nextIdx = (idx + 1) % buttons.length;
      if (key === 'Home') nextIdx = 0;
      if (key === 'End') nextIdx = buttons.length - 1;

      const nextBtn = buttons[nextIdx];
      nextBtn.focus();
      activate(nextBtn.dataset.categoryBtn);
    });

    initScrollFeatures(root);
  };

  const initAll = () => {
    document.querySelectorAll('[data-compare-bikes]').forEach(initCompareBikes);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
